/**
 * IRCD Decal Builder — Shopify Edition
 * Integrity RC Designs — Neon Circuit Theme
 *
 * Ported from WORKER-PHASE11-ULTIMATE.js to run natively on
 * the Shopify domain, eliminating the CORS cart-integration issue.
 *
 * Handles:
 * - Logo catalogue loading from Cloudflare R2
 * - Logo picker dropdowns with search
 * - Color palette rendering with media-type restrictions
 * - Skyline-based optimal nesting / bin-packing algorithm
 * - SVG buildsheet preview rendering
 * - Shopify /cart/add.js integration with line-item properties
 */

(function () {
  'use strict';

  // ════════════════════════════════════════════════════════
  // CONFIGURATION
  // ════════════════════════════════════════════════════════

  // Base URL of the ircd-assets Cloudflare Worker that serves
  // logos.json, buildsheet.svg, and individual SVG assets from R2.
  // Change this if the worker domain changes.
  var ASSETS_BASE = 'https://ircd-assets.integrityrcdesigns.workers.dev';

  // Customer email (available when logged in via Shopify)
  var CUSTOMER_EMAIL = (function () {
    var el = document.querySelector('.ircd-builder-page');
    return el ? el.getAttribute('data-customer-email') || null : null;
  })();

  // Shopify product / variant IDs for the Decal Builder product
  var VARIANT_IDS = {
    'standard':   '46905854099605',
    'chrome':     '46905854132373',
    'holo':       '46905854165141',
    'flo-orange': '46905854197909',
    'flo-yellow': '46905854230677',
    'flo-green':  '46905854263445',
    'flo-pink':   '46905854296213'
  };

  // Pricing per sheet by media type (in dollars)
  var PRICING = {
    'standard':   30.00,
    'chrome':     40.00,
    'holo':       40.00,
    'flo-orange': 40.00,
    'flo-yellow': 40.00,
    'flo-green':  40.00,
    'flo-pink':   40.00
  };

  // The 23 standard colour swatches
  var PALETTE = [
    { name: 'White',       hex: '#ffffff' },
    { name: 'Light Gray',  hex: '#d9d9d9' },
    { name: 'Gray',        hex: '#9a9a9a' },
    { name: 'Medium Gray', hex: '#666666' },
    { name: 'Dark Gray',   hex: '#2f2f2f' },
    { name: 'Black',       hex: '#000000' },
    { name: 'Red',         hex: '#ff0000' },
    { name: 'Orange',      hex: '#ff6a00' },
    { name: 'Yellow',      hex: '#ffd500' },
    { name: 'Green',       hex: '#00ff00' },
    { name: 'Light Blue',  hex: '#00c8ff' },
    { name: 'Blue',        hex: '#0055ff' },
    { name: 'Purple',      hex: '#7a00ff' },
    { name: 'Hot Pink',    hex: '#ff00c8' },
    { name: 'Mint',        hex: '#00ff9a' },
    { name: 'Dark Green',  hex: '#00b36b' },
    { name: 'Light Red',   hex: '#ff3d3d' },
    { name: 'Dark Red',    hex: '#b30000' },
    { name: 'Gold',        hex: '#ffb300' },
    { name: 'Brown',       hex: '#7a5200' },
    { name: 'Sky Blue',    hex: '#6bdcff' },
    { name: 'Rose',        hex: '#b07171' },
    { name: 'Light Pink',  hex: '#ffb3d9' }
  ];

  var MEDIA_TYPE_NAMES = {
    'standard':   'Standard',
    'chrome':     'Chrome',
    'holo':       'Holographic',
    'flo-orange': 'Fluorescent Orange',
    'flo-yellow': 'Fluorescent Yellow',
    'flo-green':  'Fluorescent Green',
    'flo-pink':   'Fluorescent Pink'
  };

  var FLUORESCENT_COLORS = {
    'flo-orange': '#ff6a00',
    'flo-yellow': '#ffd500',
    'flo-green':  '#00ff00',
    'flo-pink':   '#ff00c8'
  };

  var DEFAULT_ROWS = 7;

  // Logo sizing constraints (inches)
  var SIZE_MIN_W = 0.75;
  var SIZE_MAX_W = 6.00;
  var SIZE_DEFAULT_LARGE = 5.98;
  var SIZE_DEFAULT_SMALL = 2.00;
  var SIZE_BOX_HW_RATIO = 0.365;
  var SIZE_BOX_MIN_H = 0.50;


  // ════════════════════════════════════════════════════════
  // STATE
  // ════════════════════════════════════════════════════════

  var state = {
    logos: [],          // array of { id, name, file, role, tags }
    rows: [],           // array of { id, qty }
    colors: {
      main: null,
      outline1: null,
      outline2: null
    },
    mediaType: 'standard',
    scale: '1/8',
    sheetQuantity: 1,
    templateSvg: null,        // raw SVG text of the buildsheet template
    logoSvgCache: {},          // id -> svgText
    logoSvgFetchInFlight: {},  // id -> Promise
    lastLayout: null           // last computed placement array
  };


  // ════════════════════════════════════════════════════════
  // DOM HELPERS
  // ════════════════════════════════════════════════════════

  // Query by data-attribute shorthand
  function qa(attr) { return document.querySelectorAll('[data-' + attr + ']'); }
  function q(attr)  { return document.querySelector('[data-' + attr + ']'); }
  function el(id)   { return document.getElementById(id); }

  function normalize(s) { return (s || '').toLowerCase().trim(); }

  function pad2(n) { return String(n).padStart(2, '0'); }

  function formatMMDDYYYY(d) {
    return pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()) + '-' + d.getFullYear();
  }

  function sortAlpha(a, b) {
    return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
  }

  // Build thumbnail URL for a logo via the R2 worker
  function thumbUrl(logo) {
    var file = logo.file || '';
    var key = file.indexOf('/') !== -1 ? file : 'logos/clean/' + file;
    return ASSETS_BASE + '/r2/' + encodeURIComponent(key);
  }

  // Build full SVG URL for a logo by its id
  function logoSvgUrl(id) {
    var l = null;
    for (var i = 0; i < state.logos.length; i++) {
      if (state.logos[i].id === id) { l = state.logos[i]; break; }
    }
    if (!l) return null;
    var file = l.file || '';
    var key = file.indexOf('/') !== -1 ? file : 'logos/clean/' + file;
    return ASSETS_BASE + '/r2/' + encodeURIComponent(key);
  }


  // ════════════════════════════════════════════════════════
  // LOGO SEARCH & FILTERING
  // ════════════════════════════════════════════════════════

  function filteredLogos(queryText) {
    var qStr = normalize(queryText || '');
    var base = state.logos.slice();

    if (!qStr) return base.sort(sortAlpha);

    var tokens = qStr.split(/\s+/).filter(Boolean);

    function scoreLogo(l) {
      var name = normalize(l.name || '');
      var words = name.split(/\s+/).filter(Boolean);
      var wi = 0;
      var matchedPrefixCount = 0;

      for (var t = 0; t < tokens.length; t++) {
        var found = false;
        for (; wi < words.length; wi++) {
          if (words[wi].indexOf(tokens[t]) === 0) {
            found = true;
            matchedPrefixCount++;
            wi++;
            break;
          }
        }
        if (!found) return 0;
      }

      var firstWordBonus = (words[0] && words[0].indexOf(tokens[0]) === 0) ? 50 : 0;
      return 200 + matchedPrefixCount * 25 + firstWordBonus - Math.min(name.length, 60);
    }

    var prefixMatches = base
      .map(function (l) { return { l: l, s: scoreLogo(l) }; })
      .filter(function (x) { return x.s > 0; })
      .sort(function (a, b) { return b.s - a.s || sortAlpha(a.l, b.l); })
      .map(function (x) { return x.l; });

    if (prefixMatches.length) return prefixMatches;
    if (qStr.length === 1) return [];

    return base
      .filter(function (l) {
        var words = normalize(l.name || '').split(/\s+/).filter(Boolean);
        return words.some(function (w) { return w.indexOf(qStr) === 0; });
      })
      .sort(sortAlpha);
  }


  // ════════════════════════════════════════════════════════
  // SVG PARSING & UNITS
  // ════════════════════════════════════════════════════════

  function parseSvgLengthToInches(val) {
    if (!val) return null;
    var s = String(val).trim().toLowerCase();
    var n = parseFloat(s);
    if (!isFinite(n)) return null;
    if (s.endsWith('in')) return n;
    if (s.endsWith('cm')) return n / 2.54;
    if (s.endsWith('mm')) return n / 25.4;
    if (s.endsWith('pt')) return n / 72;
    if (s.endsWith('pc')) return (n * 12) / 72;
    if (s.endsWith('px')) return n / 96;
    return n / 96;
  }

  function getUnitsPerInchFromDoc(doc) {
    var svg = doc.querySelector('svg');
    if (!svg) return 72;

    var vb = (svg.getAttribute('viewBox') || '').trim();
    var wAttr = svg.getAttribute('width') || '';
    var hAttr = svg.getAttribute('height') || '';
    var wIn = parseSvgLengthToInches(wAttr);
    var hIn = parseSvgLengthToInches(hAttr);

    if (vb) {
      var parts = vb.split(/[\s,]+/).map(Number);
      if (parts.length === 4 && parts.every(function (n) { return isFinite(n); })) {
        var vbW = parts[2];
        var vbH = parts[3];
        var wLower = String(wAttr).trim().toLowerCase();
        var hLower = String(hAttr).trim().toLowerCase();

        var realUnits = ['in', 'cm', 'mm', 'pt', 'pc'];
        var widthIsReal = realUnits.some(function (u) { return wLower.endsWith(u); });
        var heightIsReal = realUnits.some(function (u) { return hLower.endsWith(u); });

        if (widthIsReal && wIn && vbW) return vbW / wIn;
        if (heightIsReal && hIn && vbH) return vbH / hIn;
        if (vbW > 0) return vbW / 12; // assume 12" template
      }
    }
    return 72;
  }


  // ════════════════════════════════════════════════════════
  // TEMPLATE LOADING & TEXT INJECTION
  // ════════════════════════════════════════════════════════

  function fetchTemplate() {
    return fetch(ASSETS_BASE + '/templates/buildsheet.svg', { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('Template fetch failed: ' + r.status);
        return r.text();
      })
      .then(function (text) {
        state.templateSvg = text;
      });
  }

  function injectTemplateText(svgText) {
    var nameInput = q('customer-name');
    var nameVal = (nameInput ? nameInput.value : '').trim() || 'Customer Name Here';
    var dateVal = formatMMDDYYYY(new Date());

    var parser = new DOMParser();
    var doc = parser.parseFromString(svgText, 'image/svg+xml');

    // Inject customer name
    var nameGroup = doc.getElementById('customerName');
    if (nameGroup) {
      while (nameGroup.firstChild) nameGroup.removeChild(nameGroup.firstChild);
      var text = doc.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('transform', 'translate(558.783 817.255) scale(1.348 1)');
      text.setAttribute('fill', '#231f20');
      text.setAttribute('font-family', "MyriadPro-BoldIt, 'Myriad Pro'");
      text.setAttribute('font-size', '18.581');
      text.setAttribute('font-style', 'italic');
      text.setAttribute('font-weight', '700');
      var tspan = doc.createElementNS('http://www.w3.org/2000/svg', 'tspan');
      tspan.setAttribute('x', '0');
      tspan.setAttribute('y', '0');
      tspan.textContent = nameVal;
      text.appendChild(tspan);
      nameGroup.appendChild(text);
    }

    // Inject date
    var dateGroup = doc.getElementById('dateOrdered');
    if (dateGroup) {
      while (dateGroup.firstChild) dateGroup.removeChild(dateGroup.firstChild);
      var textD = doc.createElementNS('http://www.w3.org/2000/svg', 'text');
      textD.setAttribute('transform', 'translate(558.783 851.255) scale(1.348 1)');
      textD.setAttribute('fill', '#231f20');
      textD.setAttribute('font-family', "MyriadPro-BoldIt, 'Myriad Pro'");
      textD.setAttribute('font-size', '18.581');
      textD.setAttribute('font-style', 'italic');
      textD.setAttribute('font-weight', '700');
      var tspanD = doc.createElementNS('http://www.w3.org/2000/svg', 'tspan');
      tspanD.setAttribute('x', '0');
      tspanD.setAttribute('y', '0');
      tspanD.textContent = dateVal;
      textD.appendChild(tspanD);
      dateGroup.appendChild(textD);
    }

    return doc;
  }

  function getSafeAreaFromDoc(doc) {
    var r = doc.getElementById('safeArea');
    if (!r) return null;
    var x = Number(r.getAttribute('x') || 0);
    var y = Number(r.getAttribute('y') || 0);
    var w = Number(r.getAttribute('width') || 0);
    var h = Number(r.getAttribute('height') || 0);
    if (!(w > 0 && h > 0)) return null;
    return { x: x, y: y, w: w, h: h };
  }

  function getTemplateRootSvg(doc) {
    var root = doc.documentElement;
    if (root && root.tagName && root.tagName.toLowerCase() === 'svg') return root;
    return doc.querySelector('svg') || null;
  }

  function ensureLogosLayer(doc) {
    var svg = getTemplateRootSvg(doc);
    if (!svg) return null;
    var old = doc.getElementById('ircd-logos-layer');
    if (old && old.parentNode) old.parentNode.removeChild(old);
    var g = doc.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('id', 'ircd-logos-layer');
    svg.appendChild(g);
    return g;
  }


  // ════════════════════════════════════════════════════════
  // OPTIMAL NESTING ALGORITHM (Skyline bin-packing)
  //
  // Size constraints:
  //   Large logos: max 5.98" × 0.90"
  //   Small logos: max 2.00" × 0.73"
  //   Gap: 0.10" (bleed + cut)
  // ════════════════════════════════════════════════════════

  function computeLayoutOptimal(placements, area, unitsPerInch, logoMetaById) {
    var GAP_IN = 0.10;
    var gap = unitsPerInch * GAP_IN;

    var scaleFactor = 1.0; // scale toggle removed; typed length/height are exact

    var areaRight  = area.x + area.w;
    var areaBottom = area.y + area.h;

    function fitIntoBox(meta, boxW, boxH) {
      var logoAR = meta.vbW / meta.vbH;
      var boxAR  = boxW / boxH;
      var w, h;
      if (logoAR >= boxAR) { w = boxW; h = w / logoAR; }
      else                 { h = boxH; w = h * logoAR; }
      return { w: w, h: h };
    }

    function candidatesFor(p) {
      var meta = logoMetaById[p.id];
      if (!meta) return [];

      // Custom size: p.sizeW is in inches
      var maxW = unitsPerInch * p.sizeW * scaleFactor;
      var boxH = Math.max(SIZE_BOX_MIN_H, p.sizeW * SIZE_BOX_HW_RATIO);
      var maxH = unitsPerInch * boxH * scaleFactor;

      var a0 = fitIntoBox(meta, maxW, maxH);
      var c0 = { w: a0.w, h: a0.h, rot: 0 };

      var metaR = { vbW: meta.vbH, vbH: meta.vbW };
      var a1 = fitIntoBox(metaR, maxH, maxW);
      var c1 = { w: a1.w, h: a1.h, rot: 90 };

      var area0 = c0.w * c0.h;
      var area1 = c1.w * c1.h;
      return (area1 > area0) ? [c1, c0] : [c0, c1];
    }

    function bestCand(p) {
      var c = candidatesFor(p);
      return c.length ? c[0] : null;
    }

    function keyOf(p) { return p.id + '|' + p.sizeW + '|' + p.rowIndex + '|' + p.n; }

    // ── Single skyline trial: place logos in given order, return results ──
    function runSkylineTrial(sortedPlacements) {
      var out = [];
      var used = {};
      var skyline = [{ x: area.x, y: area.y, w: area.w }];

      function checkCollision(x, y, w, h) {
        for (var i = 0; i < out.length; i++) {
          var o = out[i];
          if (!(x + w + gap <= o.x || x >= o.x + o.w + gap ||
                y + h + gap <= o.y || y >= o.y + o.h + gap)) {
            return true;
          }
        }
        return false;
      }

      function findSkylinePosition(logoW, logoH) {
        var bestPos = null;
        var bestScore = Infinity;
        var scanStep = unitsPerInch * 0.05;
        for (var scanX = area.x; scanX + logoW <= areaRight; scanX += scanStep) {
          var minY = area.y;
          for (var si = 0; si < skyline.length; si++) {
            var seg = skyline[si];
            if (seg.x < scanX + logoW && seg.x + seg.w > scanX) {
              minY = Math.max(minY, seg.y);
            }
          }
          if (minY + logoH > areaBottom) continue;
          if (checkCollision(scanX, minY, logoW, logoH)) continue;
          var score = minY * 10 + scanX * 0.1;
          if (score < bestScore) { bestScore = score; bestPos = { x: scanX, y: minY }; }
        }
        return bestPos;
      }

      function updateSkyline(x, y, w, h) {
        var logoRight = x + w;
        var logoTop = y + h + gap;
        var newSegs = [];
        var i = 0;
        while (i < skyline.length && skyline[i].x + skyline[i].w <= x) {
          newSegs.push(skyline[i]); i++;
        }
        if (i < skyline.length && skyline[i].x < x) {
          newSegs.push({ x: skyline[i].x, y: skyline[i].y, w: x - skyline[i].x });
        }
        newSegs.push({ x: x, y: logoTop, w: w });
        while (i < skyline.length && skyline[i].x < logoRight) {
          var seg = skyline[i];
          if (seg.x + seg.w > logoRight) {
            newSegs.push({ x: logoRight, y: seg.y, w: seg.x + seg.w - logoRight });
          }
          i++;
        }
        while (i < skyline.length) { newSegs.push(skyline[i]); i++; }
        skyline.length = 0;
        for (var j = 0; j < newSegs.length; j++) {
          if (skyline.length > 0) {
            var last = skyline[skyline.length - 1];
            var curr = newSegs[j];
            if (Math.abs(last.y - curr.y) < 0.01 && Math.abs(last.x + last.w - curr.x) < 0.01) {
              last.w += curr.w; continue;
            }
          }
          skyline.push(newSegs[j]);
        }
      }

      // Pass 1: Skyline placement
      for (var pi = 0; pi < sortedPlacements.length; pi++) {
        var p = sortedPlacements[pi];
        if (used[keyOf(p)]) continue;
        var cands = candidatesFor(p);
        for (var ci = 0; ci < cands.length; ci++) {
          var c = cands[ci];
          var pos = findSkylinePosition(c.w, c.h);
          if (!pos) continue;
          out.push({ id: p.id, type: p.type, rowIndex: p.rowIndex, n: p.n,
                     x: pos.x, y: pos.y, w: c.w, h: c.h, rot: c.rot });
          used[keyOf(p)] = true;
          updateSkyline(pos.x, pos.y, c.w, c.h);
          break;
        }
      }

      // Pass 2: Gap-fill using all edges
      for (var round = 0; round < 3; round++) {
        var placedAny = false;
        var unplaced = [];
        for (var ui = 0; ui < sortedPlacements.length; ui++) {
          if (!used[keyOf(sortedPlacements[ui])]) unplaced.push(sortedPlacements[ui]);
        }
        unplaced.sort(function (a, b) {
          var ca = bestCand(a), cb = bestCand(b);
          if (!ca) return 1;
          if (!cb) return -1;
          return (ca.w * ca.h) - (cb.w * cb.h);
        });

        for (var pi2 = 0; pi2 < unplaced.length; pi2++) {
          var p2 = unplaced[pi2];
          var cands2 = candidatesFor(p2);
          var best = null;
          var bestSc = Infinity;

          for (var ci2 = 0; ci2 < cands2.length; ci2++) {
            var c2 = cands2[ci2];
            var orients = [
              { w: c2.w, h: c2.h, rot: c2.rot },
              { w: c2.h, h: c2.w, rot: c2.rot === 0 ? 90 : 0 }
            ];
            for (var oi = 0; oi < orients.length; oi++) {
              var or2 = orients[oi];
              var xPos = [area.x];
              var yPos = [area.y];
              for (var ei = 0; ei < out.length; ei++) {
                xPos.push(out[ei].x + out[ei].w + gap);
                xPos.push(out[ei].x - or2.w - gap);
                yPos.push(out[ei].y + out[ei].h + gap);
                yPos.push(out[ei].y - or2.h - gap);
              }
              for (var xi = 0; xi < xPos.length; xi++) {
                var tx = xPos[xi];
                if (tx < area.x || tx + or2.w > areaRight) continue;
                for (var yi = 0; yi < yPos.length; yi++) {
                  var ty = yPos[yi];
                  if (ty < area.y || ty + or2.h > areaBottom) continue;
                  if (checkCollision(tx, ty, or2.w, or2.h)) continue;
                  var sc = ty * 10 + tx * 0.1;
                  if (sc < bestSc) {
                    bestSc = sc;
                    best = { x: tx, y: ty, w: or2.w, h: or2.h, rot: or2.rot };
                  }
                }
              }
            }
          }
          if (best) {
            out.push({ id: p2.id, type: p2.type, rowIndex: p2.rowIndex, n: p2.n,
                       x: best.x, y: best.y, w: best.w, h: best.h, rot: best.rot });
            used[keyOf(p2)] = true;
            placedAny = true;
          }
        }
        if (!placedAny) break;
      }

      return out;
    }

    // ── Score a layout: more placed logos = better, tiebreak on tighter area ──
    function scoreLayout(layout) {
      if (!layout.length) return -Infinity;
      var maxY = 0;
      for (var i = 0; i < layout.length; i++) {
        var bottom = layout[i].y + layout[i].h;
        if (bottom > maxY) maxY = bottom;
      }
      return layout.length * 100000 - maxY;
    }

    // ── 8 different sort orders for trials ──
    var sortFunctions = [
      function (a, b) {
        var ca = bestCand(a), cb = bestCand(b);
        if (!ca) return 1; if (!cb) return -1;
        var pa = ca.w + ca.h, pb = cb.w + cb.h;
        if (Math.abs(pb - pa) > 1) return pb - pa;
        return (cb.w * cb.h) - (ca.w * ca.h);
      },
      function (a, b) {
        var ca = bestCand(a), cb = bestCand(b);
        if (!ca) return 1; if (!cb) return -1;
        return (cb.w * cb.h) - (ca.w * ca.h);
      },
      function (a, b) {
        var ca = bestCand(a), cb = bestCand(b);
        if (!ca) return 1; if (!cb) return -1;
        return cb.h - ca.h;
      },
      function (a, b) {
        var ca = bestCand(a), cb = bestCand(b);
        if (!ca) return 1; if (!cb) return -1;
        return cb.w - ca.w;
      },
      function (a, b) {
        var ca = bestCand(a), cb = bestCand(b);
        if (!ca) return 1; if (!cb) return -1;
        return (cb.w / cb.h) - (ca.w / ca.h);
      },
      function (a, b) {
        var ca = bestCand(a), cb = bestCand(b);
        if (!ca) return 1; if (!cb) return -1;
        return (ca.w / ca.h) - (cb.w / cb.h);
      },
      function (a, b) {
        var ca = bestCand(a), cb = bestCand(b);
        if (!ca) return 1; if (!cb) return -1;
        return (ca.w * ca.h) - (cb.w * cb.h);
      },
      function (a, b) {
        var ca = bestCand(a), cb = bestCand(b);
        if (!ca) return 1; if (!cb) return -1;
        return Math.max(cb.w, cb.h) - Math.max(ca.w, ca.h);
      }
    ];

    // ── Run all trials, keep the best ──
    var bestLayout = null;
    var bestScore = -Infinity;

    for (var ti = 0; ti < sortFunctions.length; ti++) {
      var sorted = placements.slice().sort(sortFunctions[ti]);
      var layout = runSkylineTrial(sorted);
      var sc = scoreLayout(layout);
      if (sc > bestScore) {
        bestScore = sc;
        bestLayout = layout;
      }
    }

    // Pass 3: Rotation optimization on best result
    if (bestLayout) optimizeRotations(bestLayout, gap, areaBottom, areaRight);

    return bestLayout || [];
  }

  function optimizeRotations(placed, gap, areaBottom, areaRight) {
    for (var i = 0; i < placed.length; i++) {
      var logo = placed[i];
      var curRot = logo.rot || 0;
      var newW = curRot === 0 ? logo.h : logo.w;
      var newH = curRot === 0 ? logo.w : logo.h;

      if (logo.x + newW > areaRight || logo.y + newH > areaBottom) continue;

      var collides = false;
      for (var j = 0; j < placed.length; j++) {
        if (i === j) continue;
        var o = placed[j];
        if (!(logo.x + newW + gap <= o.x || logo.x >= o.x + o.w + gap ||
              logo.y + newH + gap <= o.y || logo.y >= o.y + o.h + gap)) {
          collides = true; break;
        }
      }
      if (collides) continue;

      var oldAspect = logo.w / logo.h;
      var newAspect = newW / newH;
      if (Math.abs(newAspect - 1.5) < Math.abs(oldAspect - 1.5) ||
          newW * newH > logo.w * logo.h * 1.05) {
        logo.w = newW;
        logo.h = newH;
        logo.rot = curRot === 0 ? 90 : 0;
      }
    }
  }


  // ════════════════════════════════════════════════════════
  // LOGO SVG FETCHING & PARSING
  // ════════════════════════════════════════════════════════

  function getLogoAspectRatio(id) {
    if (!id) return Promise.resolve(null);
    return fetchLogoSvgText(id).then(function (txt) {
      if (!txt) return null;
      var parsed = parseLogoSvg(txt);
      if (!parsed || !parsed.vbW || !parsed.vbH) return null;
      return parsed.vbW / parsed.vbH;
    });
  }

  function fetchLogoSvgText(id) {
    if (state.logoSvgCache[id]) return Promise.resolve(state.logoSvgCache[id]);
    if (state.logoSvgFetchInFlight[id]) return state.logoSvgFetchInFlight[id];

    var url = logoSvgUrl(id);
    if (!url) return Promise.resolve(null);

    var p = fetch(url, { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (txt) {
        state.logoSvgCache[id] = txt;
        delete state.logoSvgFetchInFlight[id];
        return txt;
      })
      .catch(function () {
        delete state.logoSvgFetchInFlight[id];
        return null;
      });

    state.logoSvgFetchInFlight[id] = p;
    return p;
  }

  function parseLogoSvg(svgText) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(svgText, 'image/svg+xml');
    var svg = doc.documentElement;
    if (!svg || svg.tagName.toLowerCase() !== 'svg') return null;

    var vb = svg.getAttribute('viewBox');
    var vbX = 0, vbY = 0, vbW = 0, vbH = 0;
    if (vb) {
      var parts = vb.trim().split(/[\s,]+/).map(Number);
      if (parts.length === 4 && parts.every(function (n) { return isFinite(n); })) {
        vbX = parts[0]; vbY = parts[1]; vbW = parts[2]; vbH = parts[3];
      }
    } else {
      vbW = Number(svg.getAttribute('width') || 0);
      vbH = Number(svg.getAttribute('height') || 0);
    }
    if (!(vbW > 0 && vbH > 0)) return null;

    // Compute actual bounding box by rendering offscreen.
    // Some logos have paths that extend beyond their viewBox, which causes overlap.
    try {
      var offscreen = document.createElement('div');
      offscreen.style.cssText = 'position:fixed;left:-9999px;top:-9999px;visibility:hidden;';
      document.body.appendChild(offscreen);
      var clone = svg.cloneNode(true);
      clone.setAttribute('width', '500');
      clone.setAttribute('height', '500');
      offscreen.appendChild(clone);
      var bbox = clone.getBBox();
      document.body.removeChild(offscreen);

      if (bbox && bbox.width > 0 && bbox.height > 0) {
        var PAD = 0.5;
        var realX = bbox.x - PAD;
        var realY = bbox.y - PAD;
        var realW = bbox.width + PAD * 2;
        var realH = bbox.height + PAD * 2;
        if (realX < vbX || realY < vbY ||
            realX + realW > vbX + vbW || realY + realH > vbY + vbH) {
          vbX = Math.min(vbX, realX);
          vbY = Math.min(vbY, realY);
          vbW = Math.max(vbX + vbW, realX + realW) - vbX;
          vbH = Math.max(vbY + vbH, realY + realH) - vbY;
        }
      }
    } catch (e) {
      // Fall back to declared viewBox if getBBox fails
    }

    return { svg: svg, vbX: vbX, vbY: vbY, vbW: vbW, vbH: vbH, doc: doc };
  }


  // ════════════════════════════════════════════════════════
  // LOGO COLOUR APPLICATION
  // ════════════════════════════════════════════════════════

  // Recursively set fill on shape elements
  function applyFillRecursive(node, color) {
    if (!node || node.nodeType !== 1) return;
    var tag = (node.tagName || '').toLowerCase();
    if (['path','polygon','polyline','rect','circle','ellipse','line'].indexOf(tag) !== -1) {
      node.setAttribute('fill', color);
    }
    var children = node.childNodes;
    for (var i = 0; i < children.length; i++) applyFillRecursive(children[i], color);
  }

  // Recursively apply pattern fill (chrome / holo)
  function applyPatternRecursive(node, patternId) {
    if (!node || node.nodeType !== 1) return;
    var tag = (node.tagName || '').toLowerCase();
    if (['path','polygon','polyline','rect','circle','ellipse','line'].indexOf(tag) !== -1) {
      node.setAttribute('fill', 'url(#' + patternId + ')');
    }
    var children = node.childNodes;
    for (var i = 0; i < children.length; i++) applyPatternRecursive(children[i], patternId);
  }

  function applyChromeEffect(node) {
    applyPatternRecursive(node, 'chrome-pattern');
    node.setAttribute('data-chrome', 'true');
  }

  function applyHoloEffect(node) {
    applyPatternRecursive(node, 'holo-pattern');
    node.setAttribute('data-holo', 'true');
  }

  // Walk imported logo nodes and apply user-selected colours
  function applyColorsToLogoNode(node) {
    if (!node || node.nodeType !== 1) return;
    var id = node.getAttribute && node.getAttribute('id');

    // Map SVG group ids to state.colors keys (supports suffixed variants like main1, outline1.1)
    var colorMap = { main: 'main', main1: 'main', outline1: 'outline1', 'outline1.1': 'outline1', outline2: 'outline2' };
    if (id && colorMap[id] && state.colors[colorMap[id]]) {
      var color = state.colors[colorMap[id]];
      var isChrome = color === '#ffffff' && state.mediaType === 'chrome';
      var isHolo   = color === '#ffffff' && state.mediaType === 'holo';

      if (isChrome)     applyChromeEffect(node);
      else if (isHolo)  applyHoloEffect(node);
      else              applyFillRecursive(node, color);
    }

    var children = node.childNodes;
    for (var i = 0; i < children.length; i++) applyColorsToLogoNode(children[i]);
  }

  // Load chrome & holo fill patterns and inject into SVG <defs>
  function injectChromeHoloPatterns(doc) {
    var defs = doc.createElementNS('http://www.w3.org/2000/svg', 'defs');

    function loadPattern(patternUrl, patternId) {
      return fetch(patternUrl)
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var pDoc = new DOMParser().parseFromString(text, 'image/svg+xml');
          var pSvg = pDoc.documentElement;
          var pattern = doc.createElementNS('http://www.w3.org/2000/svg', 'pattern');
          pattern.setAttribute('id', patternId);
          pattern.setAttribute('patternUnits', 'userSpaceOnUse');
          pattern.setAttribute('width', pSvg.getAttribute('width') || '100');
          pattern.setAttribute('height', pSvg.getAttribute('height') || '100');
          var children = Array.prototype.slice.call(pSvg.childNodes);
          for (var i = 0; i < children.length; i++) {
            pattern.appendChild(doc.importNode(children[i], true));
          }
          defs.appendChild(pattern);
        })
        .catch(function (err) { console.error('Pattern load failed:', patternId, err); });
    }

    return Promise.all([
      loadPattern(ASSETS_BASE + '/r2/swatches/chrome-fill.svg', 'chrome-pattern'),
      loadPattern(ASSETS_BASE + '/r2/swatches/holo-fill.svg', 'holo-pattern')
    ]).then(function () {
      var svg = doc.documentElement;
      if (svg && svg.tagName.toLowerCase() === 'svg') {
        svg.insertBefore(defs, svg.firstChild);
      }
    });
  }


  // ════════════════════════════════════════════════════════
  // LOGO INJECTION INTO SVG
  // ════════════════════════════════════════════════════════

  function injectOneLogo(doc, layer, logoSvgText, placement) {
    var parsed = parseLogoSvg(logoSvgText);
    if (!parsed) return;

    var vbX = parsed.vbX, vbY = parsed.vbY, vbW = parsed.vbW, vbH = parsed.vbH;
    var rot = placement.rot || 0;
    var effW = rot === 90 ? vbH : vbW;
    var effH = rot === 90 ? vbW : vbH;
    var s = Math.min(placement.w / effW, placement.h / effH);
    var drawnW = effW * s;
    var drawnH = effH * s;
    var tx = placement.x + (placement.w - drawnW) / 2;
    var ty = placement.y + (placement.h - drawnH) / 2;

    var g = doc.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('data-logo-id', placement.id);
    g.setAttribute('data-type', placement.type);

    if (rot === 90) {
      g.setAttribute('transform',
        'translate(' + tx.toFixed(3) + ' ' + ty.toFixed(3) + ') ' +
        'rotate(90) ' +
        'scale(' + s.toFixed(6) + ') ' +
        'translate(' + (-vbX).toFixed(3) + ' ' + (-vbY - vbH).toFixed(3) + ')');
    } else {
      g.setAttribute('transform',
        'translate(' + tx.toFixed(3) + ' ' + ty.toFixed(3) + ') ' +
        'scale(' + s.toFixed(6) + ') ' +
        'translate(' + (-vbX).toFixed(3) + ' ' + (-vbY).toFixed(3) + ')');
    }

    var children = Array.prototype.slice.call(parsed.svg.childNodes);
    for (var i = 0; i < children.length; i++) {
      var imported = doc.importNode(children[i], true);
      applyColorsToLogoNode(imported);
      g.appendChild(imported);
    }

    layer.appendChild(g);
  }


  // ════════════════════════════════════════════════════════
  // BUILD PLACEMENTS FROM ROWS
  // ════════════════════════════════════════════════════════

  function buildPlacementsFromRows() {
    var out = [];
    for (var i = 0; i < state.rows.length; i++) {
      var row = state.rows[i];
      var id = (row.id || '').trim();
      if (!id) continue;
      var qty = Math.max(1, Math.floor(Number(row.qty || 1)));
      var sizeW = (row.sizeW != null && row.sizeW > 0)
        ? row.sizeW
        : (i === 0 ? SIZE_DEFAULT_LARGE : SIZE_DEFAULT_SMALL);
      for (var n = 0; n < qty; n++) {
        out.push({ id: id, type: 'custom', sizeW: sizeW, rowIndex: i, n: n });
      }
    }
    return out;
  }

  function injectLogosIntoDoc(doc) {
    var safe = getSafeAreaFromDoc(doc);
    if (!safe) return Promise.resolve(doc);

    var placements = buildPlacementsFromRows();
    if (!placements.length) {
      state.lastLayout = [];
      return Promise.resolve(doc);
    }

    var unitsPerInch = getUnitsPerInchFromDoc(doc);

    // Store safe area and units for fill-percentage calculation later
    state.safeArea = safe;
    state.unitsPerInch = unitsPerInch;

    // Collect unique logo ids
    var uniqueIds = [];
    var seen = {};
    for (var i = 0; i < placements.length; i++) {
      if (!seen[placements[i].id]) { uniqueIds.push(placements[i].id); seen[placements[i].id] = true; }
    }

    var svgTextById = {};
    var logoMetaById = {};

    return Promise.all(uniqueIds.map(function (id) {
      return fetchLogoSvgText(id).then(function (txt) {
        if (!txt) return;
        svgTextById[id] = txt;
        var parsed = parseLogoSvg(txt);
        if (parsed) logoMetaById[id] = { vbW: parsed.vbW, vbH: parsed.vbH };
      });
    })).then(function () {
      var layout = computeLayoutOptimal(placements, safe, unitsPerInch, logoMetaById);
      state.lastLayout = layout;

      if (!layout.length) return doc;

      var layer = ensureLogosLayer(doc);
      if (!layer) return doc;

      for (var i = 0; i < layout.length; i++) {
        var txt = svgTextById[layout[i].id];
        if (txt) injectOneLogo(doc, layer, txt, layout[i]);
      }
      return doc;
    });
  }


  // ════════════════════════════════════════════════════════
  // PREVIEW RENDERING
  // ════════════════════════════════════════════════════════

  // Debounced wrapper for renderPreview - use for rapid-fire inputs (typing)
  var _renderTimer = null;
  function scheduleRender() {
    if (_renderTimer) clearTimeout(_renderTimer);
    _renderTimer = setTimeout(function () {
      _renderTimer = null;
      renderPreview();
    }, 300);
  }

  function renderPreview() {
    var host = q('preview-host');
    if (!host) return Promise.resolve();

    host.innerHTML = '';

    if (!state.templateSvg) {
      host.innerHTML = '<div style="padding:var(--space-4);color:var(--color-text-tertiary)">Template not loaded.</div>';
      return Promise.resolve();
    }

    var doc = injectTemplateText(state.templateSvg);

    var patternsPromise = (state.mediaType === 'chrome' || state.mediaType === 'holo')
      ? injectChromeHoloPatterns(doc)
      : Promise.resolve();

    return patternsPromise
      .then(function () { return injectLogosIntoDoc(doc); })
      .then(function () {
        var svgOut = new XMLSerializer().serializeToString(doc);
        host.innerHTML = svgOut;
        updatePlacementStats();
      })
      .catch(function (err) {
        console.error('Preview render failed:', err);
        // Fall back to template without logos
        var fallback = injectTemplateText(state.templateSvg);
        host.innerHTML = new XMLSerializer().serializeToString(fallback);
      });
  }

  function updatePlacementStats() {
    var totalRequested = 0;
    for (var i = 0; i < state.rows.length; i++) {
      if ((state.rows[i].id || '').trim()) totalRequested += (state.rows[i].qty || 0);
    }
    var totalPlaced = state.lastLayout ? state.lastLayout.length : 0;

    var pill = q('stats-pill');
    var textEl = q('stats-text');
    var dotEl = q('stats-dot');

    if (textEl) textEl.textContent = totalPlaced + '/' + totalRequested + ' logos placed';

    if (pill) {
      if (totalPlaced < totalRequested) {
        pill.classList.add('is-warning');
        pill.title = 'Some logos could not fit. Consider reducing quantities.';
      } else {
        pill.classList.remove('is-warning');
        pill.title = 'All logos placed successfully';
      }
    }

    if (dotEl) {
      dotEl.classList.toggle('is-ok', totalPlaced >= totalRequested && totalRequested > 0);
      dotEl.classList.toggle('is-warning', totalPlaced < totalRequested && totalRequested > 0);
    }
  }


  // ════════════════════════════════════════════════════════
  // PREVIEW CAPTURE (SVG → PNG for cart image)
  // ════════════════════════════════════════════════════════

  // Capture the current SVG preview as a PNG blob
  function capturePreviewPng() {
    return new Promise(function (resolve, reject) {
      var host = q('preview-host');
      var svgEl = host ? host.querySelector('svg') : null;
      if (!svgEl) {
        reject(new Error('No preview SVG found'));
        return;
      }

      var vb = svgEl.getAttribute('viewBox');
      var parts = vb ? vb.split(/[\s,]+/) : [];
      var svgW = parts.length === 4 ? parseFloat(parts[2]) : (parseFloat(svgEl.getAttribute('width')) || 800);
      var svgH = parts.length === 4 ? parseFloat(parts[3]) : (parseFloat(svgEl.getAttribute('height')) || 800);

      // Render at 2x for good quality, capped at 2000px wide
      var scale = Math.min(2, 2000 / svgW);
      var canvasW = Math.round(svgW * scale);
      var canvasH = Math.round(svgH * scale);

      var svgText = new XMLSerializer().serializeToString(svgEl);
      var blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      var url = URL.createObjectURL(blob);

      var img = new Image();
      img.onload = function () {
        var canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvasW, canvasH);
        URL.revokeObjectURL(url);
        canvas.toBlob(function (pngBlob) {
          if (pngBlob) {
            resolve(pngBlob);
          } else {
            reject(new Error('Canvas toBlob failed'));
          }
        }, 'image/png');
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load SVG into Image'));
      };
      img.src = url;
    });
  }

  // Upload a PNG blob to the worker and return the public URL
  function uploadPreviewPng(pngBlob) {
    var timestamp = Date.now();
    var rand = Math.random().toString(36).substring(2, 8);
    var filename = 'previews/cart-' + timestamp + '-' + rand + '.png';

    return fetch(ASSETS_BASE + '/r2/' + filename, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/png' },
      body: pngBlob
    })
    .then(function (r) {
      if (!r.ok) throw new Error('Preview upload failed: ' + r.status);
      return r.json();
    })
    .then(function (data) {
      return data.url;
    });
  }


  // ════════════════════════════════════════════════════════
  // EXPORT JSON (for /cart/add.js properties)
  // ════════════════════════════════════════════════════════

  function buildExport() {
    var nameInput = q('customer-name');
    var customerName = nameInput ? nameInput.value.trim() : '';
    var selected = state.rows.filter(function (r) { return (r.id || '').trim() !== ''; });
    var pricePerSheet = PRICING[state.mediaType] || PRICING.standard;

    return {
      version: 2,
      customerName: customerName,
      dateOrdered: formatMMDDYYYY(new Date()),
      mediaType: state.mediaType,
      scale: state.scale,
      sheetQuantity: state.sheetQuantity,
      pricing: {
        pricePerSheet: pricePerSheet,
        totalPrice: pricePerSheet * state.sheetQuantity
      },
      colors: { main: state.colors.main, outline1: state.colors.outline1, outline2: state.colors.outline2 },
      items: selected.map(function (r, idx) {
        var defaultW = (idx === 0 ? SIZE_DEFAULT_LARGE : SIZE_DEFAULT_SMALL);
        var w = (r.sizeW != null && r.sizeW > 0) ? r.sizeW : defaultW;
        return { id: r.id, qty: r.qty, sizeW: w };
      }),
      placementStats: {
        requested: state.rows.reduce(function (sum, r) { return sum + (r.qty || 0); }, 0),
        placed: state.lastLayout ? state.lastLayout.length : 0
      },
      layout: (state.lastLayout || []).map(function (p) {
        return { id: p.id, type: p.type, sizeW: p.sizeW, rowIndex: p.rowIndex, n: p.n,
                 x: p.x, y: p.y, w: p.w, h: p.h, rot: p.rot };
      })
    };
  }


  // ════════════════════════════════════════════════════════
  // DROPDOWN POSITIONING
  // ════════════════════════════════════════════════════════

  function positionFixedMenu(btnEl, menuEl, menuListEl) {
    if (!btnEl || !menuEl || !menuListEl) return;

    var r = btnEl.getBoundingClientRect();
    var scrollY = window.pageYOffset || document.documentElement.scrollTop;

    menuEl.style.left = r.left + 'px';
    menuEl.style.width = r.width + 'px';

    var gapPx = 8;
    var pad = 12;
    var spaceBelow = window.innerHeight - r.bottom - pad;
    var spaceAbove = r.top - pad;

    var FIVE_ROWS = 5 * 52 + 12;
    menuListEl.style.maxHeight = FIVE_ROWS + 'px';

    var needed = FIVE_ROWS + 18;
    var openDown = spaceBelow >= needed ? true : (spaceAbove >= needed ? false : spaceBelow >= spaceAbove);

    // position: fixed is viewport-relative, so top must be viewport-relative.
    // Adding scrollY pushes the menu off-screen on scrolled mobile pages.
    if (openDown) {
      menuEl.style.top = (r.bottom + gapPx) + 'px';
      menuEl.style.transform = 'none';
    } else {
      menuEl.style.top = (r.top - gapPx) + 'px';
      menuEl.style.transform = 'translateY(-100%)';
    }
  }

  function closeAllDropdowns(exceptEl) {
    var menus = document.querySelectorAll('.ircd-logo-menu.is-open');
    for (var i = 0; i < menus.length; i++) {
      if (exceptEl && menus[i] === exceptEl) continue;
      menus[i].classList.remove('is-open');
      menus[i].style.left = '';
      menus[i].style.top = '';
      menus[i].style.width = '';
      menus[i].style.transform = '';
    }
  }


  // ════════════════════════════════════════════════════════
  // ROW MANAGEMENT
  // ════════════════════════════════════════════════════════

  function ensureMinRows(n) {
    n = n || DEFAULT_ROWS;
    while (state.rows.length < n) state.rows.push({ id: '', qty: 1, sizeW: null });
    if (state.rows.length === 0) state.rows.push({ id: '', qty: 1, sizeW: null });
  }

  function addRow() {
    // Block if existing logos already don't fit
    if (state.lastLayout) {
      var totalReq = 0;
      for (var i = 0; i < state.rows.length; i++) {
        if ((state.rows[i].id || '').trim()) totalReq += state.rows[i].qty || 0;
      }
      if (state.lastLayout.length < totalReq) {
        IRCD.toast('Sheet is full — remove logos before adding more.', 'warning');
        return;
      }
    }
    state.rows.push({ id: '', qty: 1, sizeW: null });
    renderRows();
    renderPreview();
  }

  function clearRows() {
    state.rows = [];
    ensureMinRows(DEFAULT_ROWS);
    renderRows();
    renderPreview();
  }

  // Build one logo-row DOM element
  function buildRowCard(row, idx) {
    var item = document.createElement('div');
    item.className = 'ircd-logo-item';

    // ── Logo Picker ──
    var pick = document.createElement('div');
    pick.className = 'ircd-logo-pick';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ircd-logo-picker-btn';
    btn.title = 'Choose logo';

    var btnThumb = document.createElement('span');
    btnThumb.className = 'ircd-logo-picker-btn__thumb';
    var btnImg = document.createElement('img');
    btnImg.alt = '';
    btnThumb.appendChild(btnImg);

    var btnText = document.createElement('span');
    btnText.className = 'ircd-logo-picker-btn__name';

    var btnChev = document.createElement('span');
    btnChev.className = 'ircd-logo-picker-btn__chevron';
    btnChev.textContent = '\u25BE'; // ▾

    btn.appendChild(btnThumb);
    btn.appendChild(btnText);
    btn.appendChild(btnChev);

    var menu = document.createElement('div');
    menu.className = 'ircd-logo-menu';
    var menuOptions = document.createElement('div');
    menuOptions.className = 'ircd-logo-menu__options';
    menu.appendChild(menuOptions);

    pick.appendChild(btn);
    pick.appendChild(menu);

    // Set selected logo display
    function setSelectedLogo(id) {
      var cleanId = (id || '').trim();
      if (!cleanId) {
        row.id = '';
        btnText.textContent = '(pick a logo)';
        btnImg.src = '';
        btnImg.style.display = 'none';
        return;
      }
      var l = null;
      for (var i = 0; i < state.logos.length; i++) {
        if (state.logos[i].id === cleanId) { l = state.logos[i]; break; }
      }
      row.id = l ? l.id : '';
      btnText.textContent = l ? l.name : '(pick a logo)';
      btnImg.src = l ? thumbUrl(l) : '';
      btnImg.onload = function () { btnImg.style.display = 'block'; };
      btnImg.onerror = function () { btnImg.style.display = 'none'; };
    }

    // Build dropdown menu contents
    function rebuildMenu() {
      menuOptions.innerHTML = '';

      // Search header
      var head = document.createElement('div');
      head.className = 'ircd-logo-menu__search';
      var searchInput = document.createElement('input');
      searchInput.className = 'ircd-input';
      searchInput.type = 'text';
      searchInput.placeholder = 'Search logos...';
      searchInput.autocomplete = 'off';
      searchInput.spellcheck = false;
      head.appendChild(searchInput);
      menuOptions.appendChild(head);

      var body = document.createElement('div');
      body.style.padding = 'var(--space-1)';
      menuOptions.appendChild(body);

      function renderOptions() {
        body.innerHTML = '';

        // Blank option
        var blank = document.createElement('div');
        blank.className = 'ircd-logo-option' + (!row.id ? ' is-selected' : '');
        blank.innerHTML = '<span class="ircd-logo-option__thumb"></span><span class="ircd-logo-option__name">(blank)</span>';
        blank.onclick = function () {
          setSelectedLogo('');
          menu.classList.remove('is-open');
          renderRows();
          renderPreview();
        };
        body.appendChild(blank);

        var opts = filteredLogos(searchInput.value);
        for (var oi = 0; oi < opts.length; oi++) {
          (function (l) {
            var opt = document.createElement('div');
            opt.className = 'ircd-logo-option' + (l.id === row.id ? ' is-selected' : '');

            var t = document.createElement('span');
            t.className = 'ircd-logo-option__thumb';
            var im = document.createElement('img');
            im.alt = '';
            im.src = thumbUrl(l);
            im.onerror = function () { im.style.display = 'none'; };
            t.appendChild(im);

            var nm = document.createElement('span');
            nm.className = 'ircd-logo-option__name';
            nm.textContent = l.name;

            opt.appendChild(t);
            opt.appendChild(nm);
            opt.onclick = function () {
              setSelectedLogo(l.id);
              menu.classList.remove('is-open');
              renderRows();
              renderPreview();
            };
            body.appendChild(opt);
          })(opts[oi]);
        }
      }

      searchInput.addEventListener('input', renderOptions);
      renderOptions();
      // Skip auto-focus on touch devices to prevent iOS/Android scroll-into-view
      requestAnimationFrame(function () {
        if (!('ontouchstart' in window)) searchInput.focus();
      });
    }

    setSelectedLogo(row.id);

    btn.onclick = function (e) {
      e.preventDefault();
      e.stopPropagation();
      var willOpen = !menu.classList.contains('is-open');
      closeAllDropdowns(menu);
      if (willOpen) {
        rebuildMenu();
        menu.classList.add('is-open');
        requestAnimationFrame(function () {
          positionFixedMenu(btn, menu, menuOptions);
        });
      } else {
        menu.classList.remove('is-open');
      }
    };

    // ── Size Row (W x H with locked aspect ratio) ──
    var sizeRow = document.createElement('div');
    sizeRow.className = 'ircd-logo-size-row';
    sizeRow.style.display = 'none';

    var wLabel = document.createElement('span');
    wLabel.textContent = 'W:';
    var wInput = document.createElement('input');
    wInput.type = 'text';
    wInput.inputMode = 'decimal';
    wInput.title = 'Width (inches)';

    var xLabel = document.createElement('span');
    xLabel.textContent = 'x';

    var hLabel = document.createElement('span');
    hLabel.textContent = 'H:';
    var hInput = document.createElement('input');
    hInput.type = 'text';
    hInput.inputMode = 'decimal';
    hInput.title = 'Height (inches)';

    var lockIcon = document.createElement('span');
    lockIcon.className = 'ircd-logo-size-lock';
    lockIcon.textContent = '\uD83D\uDD12';
    lockIcon.title = 'Aspect ratio locked';

    sizeRow.appendChild(wLabel);
    sizeRow.appendChild(wInput);
    sizeRow.appendChild(xLabel);
    sizeRow.appendChild(hLabel);
    sizeRow.appendChild(hInput);
    sizeRow.appendChild(lockIcon);
    pick.appendChild(sizeRow);

    var logoAR = null;

    function updateSizeDisplay() {
      if (!row.id) { sizeRow.style.display = 'none'; return; }
      sizeRow.style.display = '';
      var defaultW = (idx === 0 ? SIZE_DEFAULT_LARGE : SIZE_DEFAULT_SMALL);
      var w = (row.sizeW != null && row.sizeW > 0) ? row.sizeW : defaultW;
      wInput.value = w.toFixed(2);
      if (logoAR && logoAR > 0) {
        hInput.value = (w / logoAR).toFixed(2);
      } else {
        hInput.value = '--';
      }
    }

    if (row.id) {
      getLogoAspectRatio(row.id).then(function (ar) {
        logoAR = ar;
        updateSizeDisplay();
      });
    }

    var origSetSelected = setSelectedLogo;
    setSelectedLogo = function (id) {
      origSetSelected(id);
      if (id) {
        getLogoAspectRatio(id).then(function (ar) {
          logoAR = ar;
          updateSizeDisplay();
        });
      } else {
        logoAR = null;
        updateSizeDisplay();
      }
    };

    wInput.onfocus = function () { wInput.select(); };
    wInput.oninput = function () {
      var raw = wInput.value.trim();
      if (raw === '') return;
      var parsed = parseFloat(raw);
      if (isNaN(parsed) || parsed < SIZE_MIN_W || parsed > SIZE_MAX_W) return;
      row.sizeW = parsed;
      if (logoAR && logoAR > 0) hInput.value = (parsed / logoAR).toFixed(2);
      scheduleRender();
    };
    wInput.onblur = function () {
      var raw = wInput.value.trim();
      var parsed = parseFloat(raw);
      if (isNaN(parsed) || parsed < SIZE_MIN_W) parsed = SIZE_MIN_W;
      else if (parsed > SIZE_MAX_W) parsed = SIZE_MAX_W;
      parsed = Math.round(parsed * 100) / 100;
      row.sizeW = parsed;
      wInput.value = parsed.toFixed(2);
      if (logoAR && logoAR > 0) hInput.value = (parsed / logoAR).toFixed(2);
      renderPreview();
    };

    hInput.onfocus = function () { hInput.select(); };
    hInput.oninput = function () {
      if (!logoAR || logoAR <= 0) return;
      var raw = hInput.value.trim();
      if (raw === '') return;
      var h = parseFloat(raw);
      if (isNaN(h) || h <= 0) return;
      var w = h * logoAR;
      if (w < SIZE_MIN_W || w > SIZE_MAX_W) return;
      row.sizeW = w;
      wInput.value = w.toFixed(2);
      scheduleRender();
    };
    hInput.onblur = function () {
      if (!logoAR || logoAR <= 0) return;
      var raw = hInput.value.trim();
      var h = parseFloat(raw);
      if (isNaN(h) || h <= 0) { updateSizeDisplay(); return; }
      var w = h * logoAR;
      if (w < SIZE_MIN_W) w = SIZE_MIN_W;
      else if (w > SIZE_MAX_W) w = SIZE_MAX_W;
      w = Math.round(w * 100) / 100;
      row.sizeW = w;
      wInput.value = w.toFixed(2);
      hInput.value = (w / logoAR).toFixed(2);
      renderPreview();
    };

    // ── Quantity Input ──
    var qtyWrap = document.createElement('div');
    qtyWrap.className = 'ircd-logo-item__qty';
    var qtyInput = document.createElement('input');
    qtyInput.type = 'text';
    qtyInput.inputMode = 'numeric';
    qtyInput.pattern = '[0-9]*';
    qtyInput.value = String(row.qty || 1);
    qtyInput.className = 'ircd-input';
    qtyInput.title = 'Quantity';
    // Select all text on focus so user can immediately type a new number
    qtyInput.onfocus = function () { qtyInput.select(); };
    // Update preview as user types, but only when value is a valid number
    qtyInput.oninput = function () {
      var raw = qtyInput.value.trim();
      if (raw === '') return;
      var parsed = Math.floor(Number(raw));
      if (isNaN(parsed) || parsed < 1) return;

      // Block increases if sheet is already full
      var oldQty = row.qty || 1;
      if (parsed > oldQty && state.lastLayout) {
        var totalReq = 0;
        for (var i = 0; i < state.rows.length; i++) {
          if ((state.rows[i].id || '').trim()) totalReq += state.rows[i].qty || 0;
        }
        if (state.lastLayout.length < totalReq) {
          IRCD.toast('Sheet is full, current logos don\'t all fit.', 'warning');
          qtyInput.value = String(oldQty);
          return;
        }
      }
      row.qty = parsed;
      scheduleRender();
    };
    // Enforce minimum of 1 when user leaves the field
    qtyInput.onblur = function () {
      var raw = qtyInput.value.trim();
      if (raw === '' || isNaN(Number(raw)) || Number(raw) < 1) {
        row.qty = 1;
        qtyInput.value = '1';
        renderPreview();
      }
    };
    qtyWrap.appendChild(qtyInput);

    // ── Delete Button ──
    var del = document.createElement('button');
    del.className = 'ircd-btn ircd-btn--danger ircd-btn--sm ircd-logo-item__del';
    del.textContent = '\u00D7'; // ×
    del.title = 'Remove row';
    del.onclick = function () {
      state.rows.splice(idx, 1);
      ensureMinRows(DEFAULT_ROWS);
      renderRows();
      renderPreview();
    };

    item.appendChild(pick);
    item.appendChild(qtyWrap);
    item.appendChild(del);
    return item;
  }

  function renderRows() {
    var list = q('logo-list');
    if (!list) return;
    list.innerHTML = '';
    for (var i = 0; i < state.rows.length; i++) {
      list.appendChild(buildRowCard(state.rows[i], i));
    }
  }


  // ════════════════════════════════════════════════════════
  // COLOUR PALETTES
  // ════════════════════════════════════════════════════════

  function getFluorescentColor(mediaType) {
    return FLUORESCENT_COLORS[mediaType] || '#ff6a00';
  }

  function canSelectChromeHolo(key) {
    // Chrome/holo represented internally as #ffffff
    if (key === 'main') return true;
    if (key === 'outline1') return state.colors.main !== '#ffffff';
    if (key === 'outline2') return state.colors.outline1 !== '#ffffff';
    return true;
  }

  function renderPalette(targetAttr, key) {
    var wrap = document.querySelector('[data-palette="' + targetAttr + '"]');
    if (!wrap) return;
    wrap.innerHTML = '';

    var mt = state.mediaType;

    // ── Chrome / Holo ──
    if (mt === 'chrome' || mt === 'holo') {
      // Render standard swatches (excluding white, which is represented by the chrome/holo button)
      for (var ci = 0; ci < PALETTE.length; ci++) {
        if (PALETTE[ci].hex === '#ffffff') continue;
        (function (color) {
          var sw = document.createElement('div');
          sw.className = 'ircd-swatch' + (state.colors[key] === color.hex ? ' is-selected' : '');
          sw.style.setProperty('--c', color.hex);
          sw.title = color.name;
          sw.onclick = function () {
            state.colors[key] = color.hex;
            renderAllPalettes();
            renderPreview();
          };
          wrap.appendChild(sw);
        })(PALETTE[ci]);
      }

      // Chrome/Holo special button
      var btnImg = document.createElement('img');
      var isChromeType = mt === 'chrome';
      btnImg.src = ASSETS_BASE + '/r2/swatches/' + (isChromeType ? 'chrome-button.svg' : 'holo-button.svg');
      btnImg.className = 'ircd-swatch-special' + (state.colors[key] === '#ffffff' || !state.colors[key] ? ' is-selected' : '');
      btnImg.title = isChromeType ? 'Chrome' : 'Holographic';

      if (!canSelectChromeHolo(key)) {
        btnImg.style.opacity = '0.3';
        btnImg.style.cursor = 'not-allowed';
        btnImg.title += ' (cannot be adjacent)';
      } else {
        btnImg.onclick = function () {
          state.colors[key] = '#ffffff';
          renderAllPalettes();
          renderPreview();
        };
      }
      wrap.appendChild(btnImg);
      return;
    }

    // ── Fluorescent ──
    if (mt.indexOf('flo-') === 0) {
      var floColor = getFluorescentColor(mt);
      var floName = mt.replace('flo-', '').replace(/^./, function (c) { return c.toUpperCase(); });

      if (key === 'main') {
        var mainOpts = [{ hex: '#000000', name: 'Black' }, { hex: floColor, name: floName }];
        for (var mi = 0; mi < mainOpts.length; mi++) {
          (function (opt) {
            var sw = document.createElement('div');
            sw.className = 'ircd-swatch' + (state.colors[key] === opt.hex ? ' is-selected' : '');
            sw.style.setProperty('--c', opt.hex);
            sw.title = opt.name;
            sw.onclick = function () {
              state.colors.main = opt.hex;
              state.colors.outline1 = (opt.hex === '#000000') ? floColor : '#000000';
              renderAllPalettes();
              renderPreview();
            };
            wrap.appendChild(sw);
          })(mainOpts[mi]);
        }
        return;
      }

      if (key === 'outline1') {
        // Auto-set: opposite of main
        var mainColor = state.colors.main || '#000000';
        var o1Color = (mainColor === '#000000') ? floColor : '#000000';
        state.colors.outline1 = o1Color;
        var sw = document.createElement('div');
        sw.className = 'ircd-swatch is-selected';
        sw.style.setProperty('--c', o1Color);
        sw.style.opacity = '0.6';
        sw.style.cursor = 'not-allowed';
        sw.title = (o1Color === '#000000' ? 'Black' : floName) + ' (auto-set)';
        wrap.appendChild(sw);
        return;
      }

      if (key === 'outline2') {
        var o2Opts = [{ hex: '#000000', name: 'Black' }, { hex: floColor, name: floName }];
        for (var o2i = 0; o2i < o2Opts.length; o2i++) {
          (function (opt) {
            var sw = document.createElement('div');
            sw.className = 'ircd-swatch' + (state.colors[key] === opt.hex ? ' is-selected' : '');
            sw.style.setProperty('--c', opt.hex);
            sw.title = opt.name;
            sw.onclick = function () {
              state.colors.outline2 = opt.hex;
              renderAllPalettes();
              renderPreview();
            };
            wrap.appendChild(sw);
          })(o2Opts[o2i]);
        }
        return;
      }
    }

    // ── Standard — full palette ──
    for (var si = 0; si < PALETTE.length; si++) {
      (function (color) {
        var sw = document.createElement('div');
        sw.className = 'ircd-swatch' + (state.colors[key] === color.hex ? ' is-selected' : '');
        sw.style.setProperty('--c', color.hex);
        sw.title = color.name;
        sw.onclick = function () {
          state.colors[key] = color.hex;
          renderAllPalettes();
          renderPreview();
        };
        wrap.appendChild(sw);
      })(PALETTE[si]);
    }
  }

  function renderAllPalettes() {
    renderPalette('main', 'main');
    renderPalette('outline1', 'outline1');
    renderPalette('outline2', 'outline2');
  }


  // ════════════════════════════════════════════════════════
  // MEDIA TYPE COLOUR LOGIC
  // ════════════════════════════════════════════════════════

  function applyMediaTypeColorLogic(mediaType) {
    if (mediaType === 'standard' || mediaType === 'chrome' || mediaType === 'holo') return;

    if (mediaType.indexOf('flo-') === 0) {
      var floColor = getFluorescentColor(mediaType);
      if (!state.colors.main || state.colors.main === '#ffffff') state.colors.main = '#000000';
      if (state.colors.main !== '#000000' && state.colors.main !== floColor) state.colors.main = '#000000';
      state.colors.outline1 = (state.colors.main === '#000000') ? floColor : '#000000';
      if (!state.colors.outline2 || (state.colors.outline2 !== '#000000' && state.colors.outline2 !== floColor)) {
        state.colors.outline2 = '#000000';
      }
    }
  }


  // ════════════════════════════════════════════════════════
  // PRICING
  // ════════════════════════════════════════════════════════

  function updatePricing() {
    var price = PRICING[state.mediaType] || PRICING.standard;
    var total = price * state.sheetQuantity;

    var perSheetEl = q('price-per-sheet');
    var totalEl = q('price-total');
    if (perSheetEl) perSheetEl.textContent = '$' + price.toFixed(2);
    if (totalEl) totalEl.textContent = '$' + total.toFixed(2);
  }


  // ════════════════════════════════════════════════════════
  // ORDER VALIDATION & CART
  // ════════════════════════════════════════════════════════

  // How full is the sheet? Returns 0-1 (ratio of placed area to safe area).
  function calculateFillPercentage() {
    if (!state.safeArea || !state.lastLayout || state.lastLayout.length === 0) return 0;
    var totalArea = state.safeArea.w * state.safeArea.h;
    if (totalArea <= 0) return 0;
    var placedArea = 0;
    for (var i = 0; i < state.lastLayout.length; i++) {
      placedArea += state.lastLayout[i].w * state.lastLayout[i].h;
    }
    return placedArea / totalArea;
  }

  // Auto-fill empty space by adding copies of already-selected logos.
  // Phase 1 (bulk): add one copy of every logo each round while they all still fit.
  // Phase 2 (top-off): once a full round fails, add logos one at a time round-robin,
  //   dropping each logo from the rotation when it can no longer fit. This packs the
  //   sheet maximally; the 0.10" GAP_IN between logos preserves bleed/crash room for
  //   downstream Illustrator processing.
  // Returns a Promise that resolves when no more copies fit.
  function autoFillSheet() {
    var activeRows = [];
    for (var i = 0; i < state.rows.length; i++) {
      if ((state.rows[i].id || '').trim()) activeRows.push(state.rows[i]);
    }
    if (activeRows.length === 0) return Promise.resolve();

    var safety = 1000;

    function totalRequested() {
      var t = 0;
      for (var j = 0; j < state.rows.length; j++) {
        if ((state.rows[j].id || '').trim()) t += state.rows[j].qty || 0;
      }
      return t;
    }
    function allFit() {
      var placed = state.lastLayout ? state.lastLayout.length : 0;
      return placed >= totalRequested();
    }

    // Phase 1 — bulk: bump every active row together while they all still fit.
    function bulkStep() {
      if (safety-- <= 0) return Promise.resolve();
      var saved = activeRows.map(function (r) { return r.qty || 1; });
      for (var i = 0; i < activeRows.length; i++) {
        activeRows[i].qty = (activeRows[i].qty || 1) + 1;
      }
      return renderPreview().then(function () {
        if (!allFit()) {
          for (var k = 0; k < activeRows.length; k++) activeRows[k].qty = saved[k];
          return renderPreview(); // undo the failed round, hand off to top-off
        }
        return bulkStep();
      });
    }

    // Phase 2 — top-off: add one copy at a time, round-robin, dropping each row that
    // can no longer fit, until no logo of any type fits anymore.
    function topOff() {
      var fillable = activeRows.slice();
      var idx = 0;
      function step() {
        if (safety-- <= 0 || fillable.length === 0) return Promise.resolve();
        if (idx >= fillable.length) idx = 0;
        var row = fillable[idx];
        var saved = row.qty || 1;
        row.qty = saved + 1;
        return renderPreview().then(function () {
          if (!allFit()) {
            row.qty = saved;
            fillable.splice(idx, 1); // this logo is maxed out
            return renderPreview().then(step);
          }
          idx++;
          return step();
        });
      }
      return step();
    }

    return bulkStep().then(topOff);
  }

  function validateOrder() {
    var errors = [];

    var nameInput = q('customer-name');
    if (!nameInput || !nameInput.value.trim()) errors.push('Customer name required');

    var hasLogos = state.rows.some(function (r) { return (r.id || '').trim() !== ''; });
    if (!hasLogos) errors.push('Add at least one logo');

    // Check all colors are selected
    var missingColors = [];
    if (!state.colors.main) missingColors.push('Main');
    if (!state.colors.outline1) missingColors.push('Outline 1');
    if (!state.colors.outline2) missingColors.push('Outline 2');
    if (missingColors.length > 0) {
      errors.push('Select colors: ' + missingColors.join(', '));
    }

    // Check all logos fit
    var totalReq = 0;
    for (var i = 0; i < state.rows.length; i++) {
      if ((state.rows[i].id || '').trim()) totalReq += state.rows[i].qty || 0;
    }
    var totalPlaced = state.lastLayout ? state.lastLayout.length : 0;
    if (totalReq > 0 && totalPlaced < totalReq) {
      errors.push('All logos must fit (' + totalPlaced + '/' + totalReq + ')');
    }

    return errors;
  }

  // Switch to a mobile tab programmatically (no-op on desktop since all panels are visible)
  function switchToTab(tabName) {
    var tabBtn = document.querySelector('.ircd-builder-tab[data-tab="' + tabName + '"]');
    if (tabBtn) tabBtn.click();
  }

  // Minimum fill ratio before prompting the customer to auto-fill. Tune as needed.
  var FILL_PROMPT_THRESHOLD = 0.75;

  function addToCart() {
    var errors = validateOrder();
    var msgEl = q('validation-msg');
    var cartBtn = q('add-to-cart');

    if (errors.length > 0) {
      if (msgEl) {
        msgEl.textContent = errors.join(' \u2022 ');
        msgEl.classList.add('is-error');
      }
      IRCD.toast(errors[0], 'error');

      // Auto-switch to the tab where the customer needs to take action
      var firstError = errors[0];
      if (firstError.indexOf('Customer name') !== -1 || firstError.indexOf('logo') !== -1) {
        switchToTab('info');
      } else if (firstError.indexOf('Select colors') !== -1) {
        switchToTab('colors');
      }
      return;
    }

    // Clear validation
    if (msgEl) { msgEl.textContent = ''; msgEl.classList.remove('is-error'); }

    // Fill check: if sheet is significantly empty, offer to fill it
    var fill = calculateFillPercentage();
    if (fill > 0 && fill < FILL_PROMPT_THRESHOLD) {
      var fillPct = Math.round(fill * 100);
      var proceed = confirm(
        'Your sheet is only ' + fillPct + '% full. Would you like to add more copies of your existing logos to fill the empty space? This is free and you will still only pay for one sheet.'
      );
      if (proceed) {
        if (cartBtn) {
          cartBtn.disabled = true;
          cartBtn.classList.add('is-loading');
          cartBtn.textContent = 'Filling sheet...';
        }
        autoFillSheet().then(function () {
          renderRows();
          if (cartBtn) {
            cartBtn.classList.remove('is-loading');
            cartBtn.disabled = false;
            cartBtn.textContent = 'Add to Cart';
          }
          proceedToCartAdd();
        });
        return;
      }
    }

    proceedToCartAdd();
  }

  // Extracted from addToCart: the actual cart-add flow (capture PNG, upload, POST).
  // Called either directly or after auto-fill completes.
  function proceedToCartAdd() {
    var msgEl = q('validation-msg');
    var cartBtn = q('add-to-cart');

    // Build configuration
    var config = buildExport();
    console.log('[IRCD] Buildsheet JSON:', JSON.stringify(config, null, 2));
    var variantId = VARIANT_IDS[config.mediaType] || VARIANT_IDS.standard;
    var savedPreviewUrl = null;

    // Show loading state
    if (cartBtn) {
      cartBtn.disabled = true;
      cartBtn.classList.add('is-loading');
      cartBtn.textContent = 'Adding...';
    }

    // Capture preview image, then add to cart
    capturePreviewPng()
      .then(function (blob) { return uploadPreviewPng(blob); })
      .catch(function (err) {
        // If preview capture fails, continue without it
        console.warn('[IRCD] Preview capture failed, adding to cart without preview:', err);
        return null;
      })
      .then(function (previewUrl) {
        savedPreviewUrl = previewUrl;
        var properties = {
          '_ircd_config': JSON.stringify(config),
          'Customer Name': config.customerName,
          'Media Type': MEDIA_TYPE_NAMES[config.mediaType] || 'Standard',
          'Scale': config.scale,
          'Sheet Quantity': String(config.sheetQuantity),
          'Logo Count': String(config.items.length)
        };
        if (previewUrl) {
          properties['_ircd_preview'] = previewUrl;
          // Visible duplicate so the proof URL appears prominently in Shopify admin
          // and in default order confirmation emails (filtered from cart/order page displays).
          properties['Proof Image'] = previewUrl;
        }

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: config.sheetQuantity,
            properties: properties
          })
        });
      })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart add failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        IRCD.toast('Added to cart!', 'success');

        // Save build to R2 for "Load Previous Build" feature
        saveBuildToR2(config, savedPreviewUrl);

        // Update cart count badge in the header
        updateCartBadge();

        // Success state on button
        if (cartBtn) {
          cartBtn.classList.remove('is-loading');
          cartBtn.classList.add('is-success');
          cartBtn.textContent = 'Added!';
          setTimeout(function () {
            cartBtn.disabled = false;
            cartBtn.classList.remove('is-success');
            cartBtn.textContent = 'Add to Cart';
          }, 2000);
        }
      })
      .catch(function (err) {
        console.error('Cart add error:', err);
        IRCD.toast('Failed to add to cart. Please try again.', 'error');

        if (cartBtn) {
          cartBtn.disabled = false;
          cartBtn.classList.remove('is-loading');
          cartBtn.textContent = 'Add to Cart';
        }
      });
  }

  // Fetch current cart count and update the header badge
  function updateCartBadge() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var badges = document.querySelectorAll('[data-cart-count]');
        for (var i = 0; i < badges.length; i++) {
          badges[i].textContent = String(cart.item_count);
          badges[i].style.display = cart.item_count > 0 ? '' : 'none';
        }
      })
      .catch(function () { /* silent fail */ });
  }


  // ════════════════════════════════════════════════════════
  // DATA LOADING
  // ════════════════════════════════════════════════════════

  function loadLogos() {
    return fetch(ASSETS_BASE + '/logos.json', { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('logos.json fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var raw = Array.isArray(data.logos) ? data.logos : [];
        var mapped = [];

        for (var i = 0; i < raw.length; i++) {
          var l = raw[i] || {};
          var idSource = l.id != null ? l.id : (l.file != null ? l.file : l.name);
          var id = String(idSource || '').toLowerCase().trim();
          if (!id) continue;

          mapped.push({
            id: id,
            name: String(l.name || idSource || 'Logo'),
            file: String(l.file || ''),
            role: String(l.role || ''),
            tags: Array.isArray(l.tags) ? l.tags : []
          });
        }

        state.logos = mapped.sort(sortAlpha);
        console.log('IRCD logos loaded:', state.logos.length);
      });
  }

  function healthCheck() {
    var dotEl = q('health-dot');
    var textEl = q('health-text');

    return fetch(ASSETS_BASE + '/_health', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (textEl) textEl.textContent = j.ok ? 'Healthy' : 'Unhealthy';
        if (dotEl)  dotEl.classList.toggle('is-ok', !!j.ok);
      })
      .catch(function () {
        if (textEl) textEl.textContent = 'Offline';
        if (dotEl)  dotEl.classList.remove('is-ok');
      });
  }


  // ════════════════════════════════════════════════════════
  // EVENT HANDLERS
  // ════════════════════════════════════════════════════════

  function bindEvents() {
    // Media type buttons
    var mediaBtns = qa('media');
    for (var mi = 0; mi < mediaBtns.length; mi++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var newMedia = btn.getAttribute('data-media');

          // Update active state
          for (var j = 0; j < mediaBtns.length; j++) mediaBtns[j].classList.remove('is-active');
          btn.classList.add('is-active');

          state.mediaType = newMedia;
          applyMediaTypeColorLogic(newMedia);
          updatePricing();
          renderAllPalettes();
          renderPreview();
        });
      })(mediaBtns[mi]);
    }

    // Scale toggle removed (decal sizes are exact via the length/height inputs).

    // Logo list buttons
    var reloadBtn = q('logos-reload');
    var addBtn    = q('logos-add');
    var clearBtn  = q('logos-clear');

    if (reloadBtn) reloadBtn.addEventListener('click', function () {
      state.logoSvgCache = {};
      loadEverything(true).then(function () {
        IRCD.toast('Logos reloaded.', 'info');
      });
    });
    if (addBtn)   addBtn.addEventListener('click', function () { addRow(); });
    if (clearBtn) clearBtn.addEventListener('click', function () { clearRows(); });

    // Customer name input → update preview
    var nameInput = q('customer-name');
    if (nameInput) nameInput.addEventListener('input', function () { scheduleRender(); });

    // Refresh preview button
    var refreshBtn = q('preview-refresh');
    if (refreshBtn) refreshBtn.addEventListener('click', function () { renderPreview(); });

    // Quantity stepper
    var qtyMinusBtn = q('qty-minus');
    var qtyPlusBtn  = q('qty-plus');
    var qtyInput    = q('qty-input');

    if (qtyMinusBtn) qtyMinusBtn.addEventListener('click', function () {
      var cur = parseInt(qtyInput.value) || 1;
      if (cur > 1) {
        qtyInput.value = cur - 1;
        state.sheetQuantity = cur - 1;
        updatePricing();
      }
    });

    if (qtyPlusBtn) qtyPlusBtn.addEventListener('click', function () {
      var cur = parseInt(qtyInput.value) || 1;
      var max = parseInt(qtyInput.max) || 20;
      if (cur < max) {
        qtyInput.value = cur + 1;
        state.sheetQuantity = cur + 1;
        updatePricing();
      }
    });

    if (qtyInput) qtyInput.addEventListener('input', function () {
      var val = parseInt(qtyInput.value) || 1;
      val = Math.max(1, Math.min(20, val));
      qtyInput.value = val;
      state.sheetQuantity = val;
      updatePricing();
    });

    // Add to Cart button
    var cartBtn = q('add-to-cart');
    if (cartBtn) cartBtn.addEventListener('click', function () { addToCart(); });

    // Load Previous Build button
    var loadBuildBtn = q('load-build-btn');
    if (loadBuildBtn) {
      loadBuildBtn.addEventListener('click', function () {
        var panel = q('builds-panel');
        if (!panel) return;
        var isOpen = panel.style.display !== 'none';
        if (isOpen) {
          panel.style.display = 'none';
          return;
        }
        panel.style.display = '';
        var listEl = q('builds-list');
        if (listEl) listEl.innerHTML = '<div style="text-align:center;padding:var(--space-4) 0;color:var(--color-text-tertiary);font-size:var(--text-sm);">Loading...</div>';
        fetchBuilds().then(function (builds) {
          renderBuildsList(builds);
        });
      });
    }

    // Global: close dropdowns on outside click / Escape
    // Guard against clicks inside the menu/button so we don't close the dropdown we just opened
    document.addEventListener('click', function (e) {
      if (e.target.closest && (e.target.closest('.ircd-logo-menu') || e.target.closest('.ircd-logo-picker-btn'))) return;
      closeAllDropdowns();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllDropdowns();
    });

    // ── Mobile tab navigation ──
    var tabs = document.querySelectorAll('.ircd-builder-tab');
    for (var ti = 0; ti < tabs.length; ti++) {
      (function (tab) {
        tab.addEventListener('click', function () {
          var target = tab.getAttribute('data-tab');

          // Update tab active states
          for (var j = 0; j < tabs.length; j++) {
            tabs[j].classList.toggle('is-active', tabs[j] === tab);
            tabs[j].setAttribute('aria-selected', tabs[j] === tab ? 'true' : 'false');
          }

          // Hide all main columns and reset order state
          var cols = document.querySelectorAll('.ircd-builder__left, .ircd-builder__middle, .ircd-builder__right');
          for (var k = 0; k < cols.length; k++) {
            cols[k].classList.remove('is-active', 'is-order-active');
          }
          var orderWrapper = document.querySelector('.ircd-order-wrapper');
          if (orderWrapper) orderWrapper.classList.remove('is-active');

          if (target === 'order') {
            // Order tab: show right column in order-only mode
            var rightCol = document.querySelector('.ircd-builder__right');
            if (rightCol) rightCol.classList.add('is-order-active');
            if (orderWrapper) orderWrapper.classList.add('is-active');
          } else {
            // Other tabs: show the matching panel normally
            var targetPanel = document.querySelector('[data-panel="' + target + '"]');
            if (targetPanel) targetPanel.classList.add('is-active');
          }
        });
      })(tabs[ti]);
    }

    // "Next" buttons on mobile - simulate a tab switch
    var nextBtns = document.querySelectorAll('[data-next-tab]');
    for (var ni = 0; ni < nextBtns.length; ni++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.getAttribute('data-next-tab');
          var targetTab = document.querySelector('.ircd-builder-tab[data-tab="' + target + '"]');
          if (targetTab) targetTab.click();
          window.scrollTo(0, 0);
        });
      })(nextBtns[ni]);
    }
  }


  // ════════════════════════════════════════════════════════
  // INITIALIZATION
  // ════════════════════════════════════════════════════════

  // ════════════════════════════════════════════════════════
  // SAVE / LOAD PREVIOUS BUILDS (R2)
  // ════════════════════════════════════════════════════════

  // Save the build config to R2 after a successful cart add (fire-and-forget)
  function saveBuildToR2(config, previewUrl) {
    if (!CUSTOMER_EMAIL) return;

    var itemSummary = config.items.slice(0, 3).map(function (item) {
      var logo = null;
      for (var i = 0; i < state.logos.length; i++) {
        if (state.logos[i].id === item.id) { logo = state.logos[i]; break; }
      }
      var name = logo ? logo.name : item.id;
      return name + ' x' + item.qty;
    }).join(', ');
    if (config.items.length > 3) itemSummary += '...';

    var mediaLabel = MEDIA_TYPE_NAMES[config.mediaType] || 'Standard';
    var dateLabel = config.dateOrdered || formatMMDDYYYY(new Date());
    var label = dateLabel + ' - ' + mediaLabel + ' - ' + itemSummary;

    var buildData = {
      config: config,
      previewUrl: previewUrl || null,
      label: label,
      email: CUSTOMER_EMAIL,
      savedAt: new Date().toISOString()
    };

    var timestamp = Date.now();
    var key = 'builds/' + encodeURIComponent(CUSTOMER_EMAIL) + '/' + timestamp + '.json';

    fetch(ASSETS_BASE + '/r2/' + key, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildData)
    }).catch(function (err) {
      console.warn('[IRCD] Build save failed (non-critical):', err);
    });
  }

  // Fetch the list of previous builds for the logged-in customer
  function fetchBuilds() {
    if (!CUSTOMER_EMAIL) return Promise.resolve([]);
    return fetch(ASSETS_BASE + '/builds/list?email=' + encodeURIComponent(CUSTOMER_EMAIL))
      .then(function (r) {
        if (!r.ok) throw new Error('Build list fetch failed');
        return r.json();
      })
      .then(function (data) { return data.builds || []; })
      .catch(function (err) {
        console.warn('[IRCD] Failed to fetch builds:', err);
        return [];
      });
  }

  // Render the builds list in the panel
  function renderBuildsList(builds) {
    var listEl = q('builds-list');
    if (!listEl) return;

    if (builds.length === 0) {
      listEl.innerHTML = '<div style="text-align:center;padding:var(--space-4) 0;color:var(--color-text-tertiary);font-size:var(--text-sm);">No previous builds found.</div>';
      return;
    }

    listEl.innerHTML = '';
    for (var i = 0; i < builds.length; i++) {
      (function (build) {
        var item = document.createElement('div');
        item.className = 'ircd-build-item';

        var label = document.createElement('span');
        label.textContent = build.label;
        item.appendChild(label);

        if (build.previewUrl) {
          var tooltip = document.createElement('div');
          tooltip.className = 'ircd-build-preview-tooltip';
          var img = document.createElement('img');
          img.src = build.previewUrl;
          img.alt = 'Build preview';
          img.loading = 'lazy';
          tooltip.appendChild(img);
          item.appendChild(tooltip);

          item.addEventListener('mouseenter', function () {
            var panel = q('builds-panel');
            if (!panel) return;
            var panelRect = panel.getBoundingClientRect();
            tooltip.style.left = (panelRect.right + 8) + 'px';
            tooltip.style.top = panelRect.top + 'px';
          });
        }

        item.addEventListener('click', function () {
          loadBuildFromR2(build.key);
        });

        listEl.appendChild(item);
      })(builds[i]);
    }
  }

  // Load a specific build from R2 and apply it to the builder
  function loadBuildFromR2(key) {
    fetch(ASSETS_BASE + '/r2/' + key)
      .then(function (r) {
        if (!r.ok) throw new Error('Build fetch failed');
        return r.json();
      })
      .then(function (data) {
        if (!data.config) throw new Error('No config in build data');
        sessionStorage.setItem('ircd_reorder_config', JSON.stringify(data.config));
        loadEverything();
        IRCD.toast('Build loaded!', 'success');
        var panel = q('builds-panel');
        if (panel) panel.style.display = 'none';
      })
      .catch(function (err) {
        console.error('[IRCD] Failed to load build:', err);
        IRCD.toast('Failed to load build.', 'error');
      });
  }


  // ════════════════════════════════════════════════════════
  // REORDER CONFIG LOADING
  // ════════════════════════════════════════════════════════

  // Check sessionStorage for a saved config from the order detail page.
  // If found, populate state and return true. Must be called after loadLogos().
  function loadReorderConfig() {
    var raw = sessionStorage.getItem('ircd_reorder_config');
    if (!raw) return false;
    sessionStorage.removeItem('ircd_reorder_config');

    try {
      var cfg = JSON.parse(raw);
    } catch (e) {
      console.warn('[IRCD] Invalid reorder config:', e);
      return false;
    }

    // Media type
    if (cfg.mediaType) state.mediaType = cfg.mediaType;

    // Scale
    state.scale = '1/8'; // scale option removed; orders fixed at 1/8

    // Sheet quantity
    if (cfg.sheetQuantity) state.sheetQuantity = cfg.sheetQuantity;

    // Colors
    if (cfg.colors) {
      state.colors.main = cfg.colors.main || null;
      state.colors.outline1 = cfg.colors.outline1 || null;
      state.colors.outline2 = cfg.colors.outline2 || null;
    }

    // Customer name (stash for DOM sync)
    state._reorderName = cfg.customerName || '';

    // Build rows from items
    if (cfg.items && cfg.items.length > 0) {
      var logoIds = {};
      for (var i = 0; i < state.logos.length; i++) logoIds[state.logos[i].id] = true;

      state.rows = [];
      var skipped = 0;
      for (var j = 0; j < cfg.items.length; j++) {
        var item = cfg.items[j];
        // Validate logo still exists
        if (!logoIds[item.id]) {
          skipped++;
          continue;
        }
        // Handle version 1 configs (size: 'large'/'small' instead of sizeW)
        var sizeW = item.sizeW || null;
        if (!sizeW && item.size) {
          sizeW = item.size === 'large' ? SIZE_DEFAULT_LARGE : SIZE_DEFAULT_SMALL;
        }
        state.rows.push({ id: item.id, qty: item.qty || 1, sizeW: sizeW });
      }
      if (skipped > 0) {
        IRCD.toast(skipped + ' logo(s) no longer available and were removed.', 'warning');
      }
    }

    console.log('[IRCD] Reorder config loaded:', cfg.customerName);
    return true;
  }

  // Sync UI button states (media type, scale, sheet qty, customer name) to match state.
  // Called after loading a reorder config since the click handlers normally set these.
  function syncActiveButtons() {
    // Media type buttons
    var mediaBtns = qa('media');
    for (var i = 0; i < mediaBtns.length; i++) {
      mediaBtns[i].classList.toggle('is-active',
        mediaBtns[i].getAttribute('data-media') === state.mediaType);
    }

    // Sheet quantity input
    var qtyInput = q('qty-input');
    if (qtyInput) qtyInput.value = state.sheetQuantity;

    // Customer name
    var nameInput = q('customer-name');
    if (nameInput && state._reorderName) {
      nameInput.value = state._reorderName;
      delete state._reorderName;
    }
  }


  function loadEverything(force) {
    return healthCheck()
      .then(function () { return loadLogos(); })
      .then(function () {
        var isReorder = loadReorderConfig();
        ensureMinRows(isReorder ? 1 : DEFAULT_ROWS);
        renderRows();
        return fetchTemplate();
      })
      .then(function () {
        syncActiveButtons();
        renderAllPalettes();
        updatePricing();
        return renderPreview();
      })
      .catch(function (err) {
        console.error('IRCD Builder load failed:', err);
        IRCD.toast('Failed to load builder data. Please refresh.', 'error');
      });
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindEvents();
      loadEverything();
    });
  } else {
    bindEvents();
    loadEverything();
  }

})();
